<template>
    <tab-layout :buttons="more.buttons" :parameters="parameters" :permissions="permissions">
        <!-- TAB TITLE -->
        <template #title>
            {{ $l10n.__('Contacts associés') }}
        </template>

        <template #content>
            <div class="ovy-auto">
                <!-- Main contact -->
                <section v-if="mainContact.length > 0" class="mb-3">
                    <h4 v-if="mainContact[0].billingContact == true" class="mt-0">
                        {{ $l10n.__('Contact principal et de facturation') }}
                    </h4>
                    <h4 v-else class="mt-0">{{ $l10n.__('Contact principal') }}</h4>
                    <div v-for="contact in mainContact" :key="contact.id" class="well mb-2">
                        <component
                            :is="`person-card${!!contact.sellsyadmin ? '-sellsyadmin' : ''}`"
                            :person="contact"
                            :permissions="permissions"
                            @clicked="switchAction"
                        />
                    </div>
                </section>

                <!-- Billing contact -->
                <section v-if="billingContact.length > 0" class="mb-3">
                    <h4 class="mt-0">{{ $l10n.__('Contact de facturation') }}</h4>
                    <div v-for="contact in billingContact" :key="contact.id" class="well mb-2">
                        <PersonCard
                            :is="`person-card${!!contact.sellsyadmin ? '-sellsyadmin' : ''}`"
                            :person="contact"
                            :permissions="permissions"
                            @clicked="switchAction"
                        />
                    </div>
                </section>

                <div class="prelative">
                    <preloader :display="loading" class="mt-3" />
                    <div
                        v-if="!loading && (otherContacts.length > 0 || archivedContacts.length > 0)"
                    >
                        <h4 class="mt-0">{{ $l10n.__('Autres contacts') }}</h4>
                        <div class="flex-column flex">
                            <section class="mb-3">
                                <div>
                                    <div
                                        v-for="contact in otherContacts"
                                        :key="contact.id"
                                        class="well mb-2"
                                    >
                                        <component
                                            :is="`person-card${
                                                !!contact.sellsyadmin ? '-sellsyadmin' : ''
                                            }`"
                                            :person="contact"
                                            :permissions="permissions"
                                            @clicked="switchAction"
                                        />
                                    </div>

                                    <section v-if="archivedContacts.length > 0">
                                        <h4>{{ $l10n.__('Contacts archivés') }}</h4>

                                        <person-card
                                            v-for="contact in archivedContacts"
                                            :key="contact.id"
                                            class="well mb-3 gray-60"
                                            :person="contact"
                                            :permissions="permissions"
                                            @clicked="switchAction"
                                        />
                                    </section>

                                    <CompassButton
                                        v-if="infiniteLoading"
                                        :label="$t.__('common.actions.load_more')"
                                        :loading="loading"
                                        @click="fetch"
                                    />
                                </div>
                            </section>
                        </div>
                    </div>
                    <div v-else>
                        <div v-if="!loading && contacts.length == 0" class="placeholder">
                            <p>{{ placeholder }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </tab-layout>
</template>

<script>
import OverviewTabMixin from './OverviewTabMixin';
import {
    contactsActions,
    mainContactsActions,
    sellsyAdminContactActions,
} from './mapping/ThirdMapping';
import { buildUrl, formatActions } from '../../../helpers';
import PersonCardSellsyadmin from '../../sellsyadmin/PersonCardSellsyadmin.vue';
import TabSellsyadminContactsMixins from './mixins/TabSellsyadminContactsMixins';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import TabLayout from 'vcomponents/overview/tabs/TabLayout.vue';
import PersonCard from 'vcomponents/utilities/PersonCard.vue';

export default {
    components: {
        Preloader,
        TabLayout,
        PersonCard,
        PersonCardSellsyadmin,
    },
    mixins: [OverviewTabMixin, TabSellsyadminContactsMixins],

    data() {
        return {
            loading: false,
            contacts: [],
            permissions: {},
            infiniteLoading: false,
            actions: contactsActions,
            mainActionsContact: mainContactsActions,
            sellsyAdminContactActions: sellsyAdminContactActions,
            page: 1,
            lastCallEmpty: false,
        };
    },

    computed: {
        mainContact() {
            return this.contacts.filter(
                (contact) => contact.mainContact == true && contact.isArchived == false,
            );
        },

        billingContact() {
            return this.contacts.filter(
                (contact) =>
                    contact.billingContact == true &&
                    contact.isArchived == false &&
                    contact.mainContact == false,
            );
        },

        otherContacts() {
            return this.contacts.filter(
                (contact) =>
                    contact.mainContact == false &&
                    contact.billingContact == false &&
                    contact.isArchived == false,
            );
        },
        archivedContacts() {
            return this.contacts.filter(
                (contact) => contact.mainContact == false && contact.isArchived == true,
            );
        },

        parameters() {
            return [
                {
                    key: 'id',
                    value: this.$store.getters.linkedid,
                },
            ];
        },
        placeholder() {
            return this.$l10n.__('Aucun contact associé');
        },
    },

    created() {
        if (
            this.$route.name === 'third.overview.contacts' &&
            !this.$store.getters.permissions.can_show_contacts
        ) {
            this.$router.push({ name: 'third.overview.informations' });
            return;
        }
        const mainContact = this.$store.getters.mainContact;
        if (mainContact) {
            mainContact.actions = this.setActions(mainContact);
            this.contacts.push(mainContact);
        }
        this.fetch();
    },

    methods: {
        buildUrl,
        formatActions,
        defineMainContact(contact) {
            this.$ajax.put(
                `/rest/${this.linkedtype}s/${this.linkedid}/contacts/main`,
                {},
                { contact: contact.id },
                (response) => {
                    this.$store.dispatch('updateMainContact', contact);
                    this.contacts.map((oneContact) => {
                        oneContact.mainContact = false;
                        if (contact.id == oneContact.id) {
                            oneContact.mainContact = true;
                        }
                        oneContact.actions = this.setActions(oneContact);
                    });
                },
                (error) => {
                    Push.error(
                        this.$l10n.__(
                            "Une erreur s'est produite lors de la sauvegarde du contact principal",
                        ),
                        false,
                    );
                },
            );
        },

        defineBillingContact(contact) {
            this.$ajax.put(
                `/rest/${this.linkedtype}s/${this.linkedid}/contacts/billing`,
                {},
                { contact: contact.id },
                (response) => {
                    this.contacts.map((oneContact) => {
                        oneContact.billingContact = false;
                        if (contact.id == oneContact.id) {
                            oneContact.billingContact = true;
                        }
                        oneContact.actions = this.setActions(oneContact);
                    });
                },
                (error) => {
                    Push.error(
                        this.$l10n.__(
                            "Une erreur s'est produite lors de la sauvegarde du contact de facturation",
                        ),
                        false,
                    );
                },
            );
        },

        defineDunningContact(contact) {
            this.$ajax.put(
                `/rest/${this.linkedtype}s/${this.linkedid}/contacts/dunning`,
                {},
                { contact: contact.id },
                (response) => {
                    this.contacts.map((oneContact) => {
                        oneContact.mainContactDunning = false;
                        if (contact.id == oneContact.id) {
                            oneContact.mainContactDunning = true;
                        }
                        oneContact.actions = this.setActions(oneContact);
                    });
                    Push.success(
                        this.$l10n.__('Le contact de relance a bien été mis à jour.'),
                        false,
                    );
                },
                (error) => {
                    Push.error(
                        this.$l10n.__(
                            "Une erreur s'est produite lors de la sauvegarde du contact de relance",
                        ),
                        false,
                    );
                },
            );
        },

        fetch($state) {
            if (this.otherContacts.length == 0) {
                this.loading = true;
            }
            this.$ajax.get(
                `/rest/${this.linkedtype}s/${this.linkedid}/contacts`,
                { page: this.page },
                (response) => {
                    this.permissions = response.permissions;
                    if (response.data.length > 0) {
                        _.each(response.data, (contact) => {
                            if (!contact.mainContact) {
                                contact.actions = this.setActions(contact);
                                this.contacts.push(contact);
                            }
                        });
                        this.page++;
                        this.infiniteLoading = response.nbrPages > response.page;

                        if ($state) {
                            $state.loaded();
                        }
                    } else {
                        if ($state) {
                            $state.complete();
                        }
                    }
                    this.loading = false;
                },
                (error) => {
                    Push.error(
                        this.$l10n.__("Une erreur s'est produite lors du chargement des données."),
                        false,
                    );
                },
            );
        },

        setActions(contact) {
            const contactParameters = [
                {
                    key: 'id',
                    value: contact.peopleId,
                },
                {
                    key: 'contactid',
                    value: contact.id,
                },
                {
                    key: 'csrfToken',
                    value: contact.csrfToken,
                },
                {
                    key: 'staffid',
                    value: contact.sellsyadmin ? contact.sellsyadmin.staff_id : false,
                },
                {
                    key: 'corpid',
                    value: contact.sellsyadmin ? contact.sellsyadmin.staff_corpid : false,
                },
                {
                    key: 'linkedid',
                    value: this.linkedid,
                },
            ];
            let actions = contact.mainContact ? this.mainActionsContact : this.actions;
            actions = this.feedSellsyAdminActions(contact, actions);

            return this.formatActions(actions, contact, contactParameters);
        },

        switchAction(action, contact) {
            switch (action) {
                case 'defineMainContact':
                    this.defineMainContact(contact);
                    break;
                case 'defineDunningContact':
                    this.defineDunningContact(contact);
                    break;
                case 'defineAsCorporationOwner':
                    if (null == contact.sellsyadmin) return;
                    this.defineAsCorporationOwner(contact);
                    break;

                case 'defineBillingContact':
                    this.defineBillingContact(contact);
                    break;
            }
        },

        feedSellsyAdminActions(contact, actions) {
            const sellsyAdminContactActionsAvailable = [];
            if (!contact.mainContact) {
                if (
                    contact.sellsyadmin &&
                    (contact.sellsyadmin.staff_type == 'regular' ||
                        contact.sellsyadmin.staff_type == 'temporary')
                ) {
                    this.sellsyAdminContactActions.map((SAAction) => {
                        if (
                            SAAction.id == 'isDecisionMaker' ||
                            SAAction.id == 'isNotDecisionMaker'
                        ) {
                            contact.sellsyadmin.staff_isDecisionMaker &&
                            SAAction.id == 'isNotDecisionMaker'
                                ? sellsyAdminContactActionsAvailable.push(SAAction)
                                : false;
                            !contact.sellsyadmin.staff_isDecisionMaker &&
                            SAAction.id == 'isDecisionMaker'
                                ? sellsyAdminContactActionsAvailable.push(SAAction)
                                : false;
                        } else if (SAAction.id == 'blockStaff' || SAAction.id == 'unblockStaff') {
                            if (contact.sellsyadmin.staff_ownerid == '0') {
                                false;
                            } else {
                                contact.sellsyadmin.staff_status == 'ok' &&
                                SAAction.id == 'blockStaff'
                                    ? sellsyAdminContactActionsAvailable.push(SAAction)
                                    : false;
                                contact.sellsyadmin.staff_status == 'blocked' &&
                                SAAction.id == 'unblockStaff'
                                    ? sellsyAdminContactActionsAvailable.push(SAAction)
                                    : false;
                            }
                        } else if (
                            SAAction.id == 'staffType' &&
                            contact.sellsyadmin.staff_ownerid == '0'
                        ) {
                            return; // action is unavailable
                        } else if (
                            SAAction.id == 'changeCorpOwner' &&
                            contact.sellsyadmin.staff_ownerid == '0'
                        ) {
                            return; // action unavailable
                        } else {
                            sellsyAdminContactActionsAvailable.push(SAAction);
                        }
                    });
                }
            }
            if (
                sellsyAdminContactActionsAvailable &&
                sellsyAdminContactActionsAvailable.length > 0 &&
                contact.sellsyadmin
            ) {
                return [...actions, ...sellsyAdminContactActionsAvailable];
            } else {
                return actions;
            }
        },
    },
};
</script>
